import { Injectable, inject } from '@angular/core';
import { WINDOW } from '@shared/dom';
import { CompatibilityType } from '@shared/services/browser-compatibility/compatibility.enum';

@Injectable({
  providedIn: 'root',
})
export class BrowserCompatibilityService {
  private readonly window = inject<Window>(WINDOW);

  private readonly facebookPointers = /FBAN|FBAV/;
  // Source: http://detectmobilebrowsers.com/
  // eslint-disable-next-line max-len
  private readonly mobileRegexSimple =
    /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i;
  private readonly webViewPointers = /\bwv\b/;

  /**
   * Check if there's compatibility issues with the user's browser.
   * @returns A compatibility type if there's a compatibility issue, or null if there's no issues.
   */
  public async checkBrowserSupport(): Promise<CompatibilityType | null> {
    if (this.isAndroidWebView()) {
      return CompatibilityType.AndroidWebView;
    } else if (!this.hasGetUserMedia()) {
      return CompatibilityType.Unsupported;
    } else {
      return null;
    }
  }

  /**
   * Returns true if we're not on a recognised mobile device according to the user agent.
   *
   * Note this doesn't guarantee that we are, in fact, on a desktop device—it just means we can't prove it's mobile.
   *
   * @returns True if we suspect we're on a desktop device.
   */
  public isDesktop(): boolean {
    // window.opera is a last resort to get userAgent on opera browsers.
    const agent = this.window.navigator.userAgent || this.window.navigator.vendor || (this.window as any).opera;
    return !this.mobileRegexSimple.test(agent);
  }

  private isAndroidWebView(): boolean {
    // NB: See https://developer.chrome.com/multidevice/user-agent#webview_user_agent
    return this.webViewPointers.test(this.window.navigator.userAgent);
  }

  private hasGetUserMedia(): boolean {
    return typeof this.window.navigator.mediaDevices?.getUserMedia !== 'undefined';
  }
}
